import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import type {
  IDataService,
  PlatformAdminDashboard,
  UpdatePlatformTenantInput,
} from '@erp/data-access/interfaces';

export const PLATFORM_ADMIN_ACCESS_KEY = ['platform-admin', 'access'] as const;
export const PLATFORM_ADMIN_DASHBOARD_KEY = ['platform-admin', 'dashboard'] as const;
const PLATFORM_ADMIN_QUERY_TIMEOUT_MS = 12_000;
const PLATFORM_ADMIN_ACCESS_TIMEOUT_MS = 8_000;

export function fetchPlatformAdminAccess(ds: IDataService): Promise<boolean> {
  return withPlatformAdminQueryTimeout(
    ds.platformAdmin.isAdmin(),
    'platform admin access',
    PLATFORM_ADMIN_ACCESS_TIMEOUT_MS,
  );
}

export function fetchPlatformAdminDashboard(ds: IDataService): Promise<PlatformAdminDashboard> {
  return withPlatformAdminQueryTimeout(
    ds.platformAdmin.getDashboard(),
    'platform admin dashboard',
    PLATFORM_ADMIN_QUERY_TIMEOUT_MS,
  );
}

export function usePlatformAdminAccess(enabled = true) {
  const ds = useDataService();
  return useQuery({
    queryKey: PLATFORM_ADMIN_ACCESS_KEY,
    queryFn: () => fetchPlatformAdminAccess(ds),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
}

export function usePlatformAdminDashboard(enabled = true) {
  const ds = useDataService();
  return useQuery({
    queryKey: PLATFORM_ADMIN_DASHBOARD_KEY,
    queryFn: () => fetchPlatformAdminDashboard(ds),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useCreatePlatformTenantView() {
  const ds = useDataService();
  return useMutation({
    mutationFn: (tenantId: string) => ds.platformAdmin.createTenantView(tenantId),
  });
}

export function useUpdatePlatformTenant() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      patch,
    }: {
      tenantId: string;
      patch: UpdatePlatformTenantInput;
    }) => ds.platformAdmin.updateTenant(tenantId, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ADMIN_DASHBOARD_KEY });
    },
  });
}

export function useSetPlatformTenantModule() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      moduleKey,
      isEnabled,
    }: {
      tenantId: string;
      moduleKey: string;
      isEnabled: boolean;
    }) => ds.platformAdmin.setModule(tenantId, moduleKey, isEnabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ADMIN_DASHBOARD_KEY });
    },
  });
}

function withPlatformAdminQueryTimeout<T>(
  promise: Promise<T>,
  label: string,
  timeoutMs: number,
): Promise<T> {
  if (typeof window === 'undefined') return promise;

  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`${label} timeout`));
    }, timeoutMs);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}
